import {
  Air as OxygenIcon,
  CalendarMonth as CalendarIcon,
} from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useQuery } from "@tanstack/react-query";
import { subDays, format, startOfMonth, subMonths } from "date-fns";
import React, { useState, useMemo } from "react";
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
} from "recharts";

import { activityApi } from "../../../../apis";
import {
  MetricValuesAggregationRequestDto,
  MetricValuesAggregationRequestDtoIntervalEnum,
  MetricValuesAggregationRequestDtoAggregateTypesEnum,
  MetricValuesAggregationRequestDtoHealthProviderCodeEnum,
  MetricValuesAggregationRequestDtoMetricCodeEnum,
} from "../../../../libs/cellera-sdk";
import { useCelleraTheme } from "../../../../theme/useCelleraTheme";

type TimeRange = "1D" | "1W" | "1M" | "6M" | "1Y" | "CUSTOM";

interface Props {
  patientId: string;
}

const BloodOxygenChart: React.FC<Props> = ({ patientId }) => {
  const { colors } = useCelleraTheme();
  const [selectedTimeRange, setSelectedTimeRange] = useState<TimeRange>("1W");
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  /** 🗓️ Date range calculation */
  const getDateRange = (timeRange: TimeRange, customDate?: Date) => {
    const endDate = new Date();
    const startDate = new Date();

    switch (timeRange) {
      case "CUSTOM":
        if (customDate) {
          startDate.setTime(customDate.getTime());
          startDate.setHours(0, 0, 0, 0);
          endDate.setTime(customDate.getTime());
          endDate.setHours(23, 59, 59, 999);
        }
        break;
      case "1D":
        startDate.setHours(0, 0, 0, 0);
        break;
      case "1W":
        startDate.setDate(endDate.getDate() - 7);
        break;
      case "1M":
        startDate.setDate(endDate.getDate() - 30);
        break;
      case "6M":
        startDate.setMonth(endDate.getMonth() - 6);
        break;
      case "1Y":
        startDate.setFullYear(endDate.getFullYear() - 1);
        break;
    }

    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };
  };

  const { startDate, endDate } = getDateRange(
    selectedTimeRange,
    selectedDate || undefined
  );

  /** 📊 Fetch blood oxygen data */
  const { data: metricData, isLoading } = useQuery({
    queryKey: ["bloodOxygenChart", patientId, selectedTimeRange, selectedDate],
    queryFn: async () => {
      if (!patientId) return null;
      const interval =
        selectedTimeRange === "6M" || selectedTimeRange === "1Y"
          ? MetricValuesAggregationRequestDtoIntervalEnum.Monthly
          : MetricValuesAggregationRequestDtoIntervalEnum.Daily;

      const request: MetricValuesAggregationRequestDto = {
        metricCode:
          MetricValuesAggregationRequestDtoMetricCodeEnum.BloodOxygenSaturation,
        healthProviderCode:
          MetricValuesAggregationRequestDtoHealthProviderCodeEnum.AppleHealthkit,
        interval,
        aggregateTypes: [
          MetricValuesAggregationRequestDtoAggregateTypesEnum.LatestRecord,
        ],
        startDate,
        endDate,
        orderBy: "ASC",
      };

      const response = await activityApi.getMetricValuesAggregatedByPatientId({
        patientId,
        metricValuesAggregationRequestDto: request,
      });
      return response;
    },
    enabled: !!patientId,
    refetchOnWindowFocus: false,
  });

  /** 🧮 Chart data builder */
  const chartData = useMemo(() => {
    if (!metricData?.data) return [];

    const dataMap: Record<string, number> = {};
    for (const d of metricData.data) {
      const key = format(new Date(d.startDate), "yyyy-MM-dd");
      dataMap[key] = d.valueIntData?.latest ?? d.valueFloatData?.latest ?? 0;
    }

    const today = new Date();

    if (selectedTimeRange === "1D") {
      const key = format(today, "yyyy-MM-dd");
      const value = dataMap[key] ?? 0;
      return [
        {
          date: format(today, "MMM d, yyyy"),
          value,
          isCurrent: true,
        },
      ];
    }

    if (selectedTimeRange === "1W") {
      const days = [];
      for (let i = 6; i >= 0; i--) {
        const date = subDays(today, i);
        const key = format(date, "yyyy-MM-dd");
        days.push({
          date: format(date, "EEE").slice(0, 2), // Mo, Tu, We...
          value: dataMap[key] ?? 0,
          isCurrent: i === 0, // Most recent day
        });
      }
      return days;
    }

    if (selectedTimeRange === "1M") {
      const days = [];
      for (let i = 30; i >= 0; i--) {
        const date = subDays(today, i);
        const key = format(date, "yyyy-MM-dd");
        days.push({
          date: format(date, "dd"),
          value: dataMap[key] ?? 0,
          isCurrent: i === 0, // Most recent day
        });
      }
      return days;
    }

    if (selectedTimeRange === "6M") {
      const months = [];
      for (let i = 5; i >= 0; i--) {
        const date = subMonths(today, i);
        const key = format(startOfMonth(date), "yyyy-MM");
        const total = Object.keys(dataMap)
          .filter((d) => d.startsWith(key))
          .reduce((sum, k) => sum + (dataMap[k] ?? 0), 0);
        const count = Object.keys(dataMap).filter((d) =>
          d.startsWith(key)
        ).length;
        months.push({
          date: format(date, "MMM"),
          value: count > 0 ? total / count : 0, // Average for the month
          isCurrent: i === 0, // Most recent month
        });
      }
      return months;
    }

    if (selectedTimeRange === "1Y") {
      const months = [];
      for (let i = 11; i >= 0; i--) {
        const date = subMonths(today, i);
        const key = format(startOfMonth(date), "yyyy-MM");
        const total = Object.keys(dataMap)
          .filter((d) => d.startsWith(key))
          .reduce((sum, k) => sum + (dataMap[k] ?? 0), 0);
        const count = Object.keys(dataMap).filter((d) =>
          d.startsWith(key)
        ).length;
        months.push({
          date: format(date, "MMM").charAt(0),
          value: count > 0 ? total / count : 0, // Average for the month
          isCurrent: i === 0, // Most recent month
        });
      }
      return months;
    }

    if (selectedTimeRange === "CUSTOM" && selectedDate) {
      const key = format(selectedDate, "yyyy-MM-dd");
      const value = dataMap[key] ?? 0;
      return [
        {
          date: format(selectedDate, "MMM d, yyyy"),
          value,
          isCurrent: true,
        },
      ];
    }

    return [];
  }, [metricData, selectedTimeRange, selectedDate]);

  // Calculate range and current value
  const { min, max, current } = useMemo(() => {
    if (chartData.length === 0) return { min: 0, max: 0, current: 0 };

    const values = chartData.map((d) => d.value).filter((v) => v > 0);
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const currentValue = chartData.find((d) => d.isCurrent)?.value ?? 0;

    return {
      min: Math.round(minValue),
      max: Math.round(maxValue),
      current: Math.round(currentValue),
    };
  }, [chartData]);

  const ranges: TimeRange[] = ["1D", "1W", "1M", "6M", "1Y"];

  if (isLoading) {
    return (
      <Card sx={{ borderRadius: 2, border: "1px solid #E8E8E9", p: 2 }}>
        <Typography variant="body2">Loading blood oxygen data...</Typography>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        borderRadius: 2,
        border: "1px solid #C6C5D5",
        p: 2,
        width: "100%",
        height: 516,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardContent
        sx={{ p: 0, flex: 1, display: "flex", flexDirection: "column", pb: 0 }}
      >
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <OxygenIcon sx={{ color: colors.primary.main, fontSize: 22 }} />
            <Typography
              variant="body1"
              fontWeight={500}
              color="text.primary"
              sx={{ fontSize: "0.875rem" }}
            >
              Blood Oxygen
            </Typography>
          </Box>

          {/* Calendar Date Picker */}
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              open={isDatePickerOpen}
              onClose={() => {
                setIsDatePickerOpen(false);
                setAnchorEl(null);
              }}
              value={selectedDate}
              onChange={(newValue) => {
                setSelectedDate(newValue);
                setSelectedTimeRange("CUSTOM");
                setIsDatePickerOpen(false);
                setAnchorEl(null);
              }}
              maxDate={new Date()}
              slotProps={{
                popper: {
                  anchorEl: anchorEl,
                  placement: "bottom-end",
                  sx: {
                    zIndex: 1300,
                  },
                },
                textField: {
                  sx: { display: "none" },
                },
              }}
            />
            <IconButton
              ref={(el) => setAnchorEl(el)}
              onClick={(event) => {
                setIsDatePickerOpen(true);
                setAnchorEl(event.currentTarget);
              }}
            >
              <CalendarIcon sx={{ fontSize: 18, color: colors.primary.main }} />
            </IconButton>
          </LocalizationProvider>
        </Box>

        {/* Time range selector */}
        <Box
          display="flex"
          justifyContent="space-between"
          mb={1}
          sx={{
            padding: "4px",
            width: "100%",
            height: "40px",
            background: "#F6F6F6",
            borderRadius: "14px",
            alignItems: "center",
          }}
        >
          {ranges.map((range) => (
            <Button
              key={range}
              variant="text"
              onClick={() => setSelectedTimeRange(range)}
              sx={{
                p: "6px 8px",
                height: 32,
                minHeight: 32,
                bgcolor:
                  selectedTimeRange === range ? "#FFFFFF" : "transparent",
                border:
                  selectedTimeRange === range ? "1px solid #E8E8E9" : "none",
                borderRadius: "10px",
                flex: 1,
                textTransform: "none",
                fontWeight: 500,
                fontSize: "12px",
                color: "#000000",
                "&:hover": {
                  bgcolor:
                    selectedTimeRange === range
                      ? "#FFFFFF"
                      : "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              {range}
            </Button>
          ))}
        </Box>

        {/* Range and Current Display */}
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "0.75rem" }}
          >
            RANGE {min}-{max}%
          </Typography>
          <Typography variant="h6" fontWeight={600} color="text.primary">
            {current}%
          </Typography>
        </Box>

        {/* Chart */}
        <Box sx={{ flex: 1, width: "100%", mt: 1, mb: 0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart data={chartData}>
              <CartesianGrid stroke="#E8E8E9" strokeWidth={1} />
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                interval={selectedTimeRange === "1M" ? 6 : 0}
              />
              <YAxis tickLine={false} axisLine={false} domain={[80, 100]} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#fff",
                  borderColor: "#E8E8E9",
                  borderRadius: "8px",
                }}
                formatter={(value: number) => [
                  `${Math.round(value)}%`,
                  "Blood Oxygen",
                ]}
              />
              <Scatter dataKey="value">
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={
                      entry.isCurrent
                        ? colors.primary.main
                        : colors.text.secondary
                    }
                    r={entry.isCurrent ? 6 : 3}
                  />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

export default BloodOxygenChart;
